import React, { useRef, useEffect, useState } from "react";
import { Image } from "../../../core";
import { getMenuItems } from "./constant";
import { popupClickOutside } from "../../../../utils/outsideClick";
import { useNavigate, useLocation } from "react-router-dom";
import Token from "../../../shared/Token";
import { useNotificationContext } from "../../../../components/NotificationProvider";
import TitleWithLines from "../../../shared/TitleWithLines";

type Props = {
  onClose: VoidFunction;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Sidemenu({
  onClose,
  isCollapsed,
  setIsCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hover, setHover] = useState<string | null>(null);
  const ref = useRef(null);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { unreadCount } = useNotificationContext();
  const [isMobile, setIsMobile] = useState(false);
  const touchStartXRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const items = await getMenuItems();
        setMenuItems(items);
      } catch (error) {
        console.error("Error fetching menu items:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 0) return;
      touchStartXRef.current = event.touches[0].clientX;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (touchStartXRef.current === null) return;
      if (event.changedTouches.length === 0) return;

      const touchEndX = event.changedTouches[0].clientX;
      const startX = touchStartXRef.current;
      const deltaX = startX - touchEndX;

      const opened = isMobileMenuOpen;

      if (!opened && startX > window.innerWidth - 60 && deltaX > 50) {
        setIsMobileMenuOpen(true);
      }

      if (opened && deltaX < -50) {
        setIsMobileMenuOpen(false);
      }

      touchStartXRef.current = null;
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, isMobileMenuOpen]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const cleanup = popupClickOutside(ref, () => {
      if (isMobile) {
        setIsMobileMenuOpen(false);
      }
      onClose();
    });

    return cleanup;
  }, [isMobile, onClose]);

  const closeMobileMenu = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
    onClose();
  };

  const MOBILE_MENU_WIDTH = 220;
  const containerWidth = isMobile ? MOBILE_MENU_WIDTH : isCollapsed ? 110 : 220;

  const handleChevronClick = () => {
    if (isMobile) {
      setIsMobileMenuOpen((prev) => !prev);
      return;
    }

    setIsCollapsed(!isCollapsed);
  };

  const renderIcon = (item: any) => {
    const isActive = item.route === location.pathname;

    // If icon is a string (SVG path), render as Image component
    if (typeof item.icon === "string") {
      // Special handling for notification icon
      // let iconClasses = "w-6 h-6";

      // Use filled version if available and item is active
      const iconSrc = isActive && item.iconFill ? item.iconFill : item.icon;

      // if (isNotificationIcon) {
      //   // Notification icon: white when active, #9F9F9F when inactive
      //   iconClasses += isActive ? " filter brightness-0 invert" : "";
      // } else {
      //   // Other icons: use existing logic only if no filled version
      //   if (!item.iconFill) {
      //     iconClasses += isActive ? " filter brightness-0 invert" : "";
      //   }
      // }

      return (
        <div
          className={`flex items-center w-full ${
            isCollapsed && !isMobile ? "gap-0 w-[24px] h-[24px]" : "gap-2"
          }`}
          style={{
            justifyContent: isCollapsed && !isMobile ? "center" : "flex-start",
          }}
        >
          <div className="relative">
            <Image
              src={iconSrc}
              alt={item.label}
              // className={iconClasses}
              // style={
              //   isNotificationIcon && !isActive
              //     ? {
              //         filter:
              //           "brightness(0) saturate(100%) invert(62%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(94%) contrast(88%)",
              //       }
              //     : undefined
              // }
            />
            {/* Notification badge for notifications menu item */}
            {/* {item.route === "/notifications" && unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                {unreadCount > 99 ? "99+" : unreadCount}
              </div>
            )} */}
          </div>
          <p
            className={`text-BodyText/16/Light flex text-gray-500 transition-all duration-300 ease-in-out ${
              isActive ? "text-gray-700" : ""
            } ${hover === item.label ? "text-gray-700" : ""} ${
              isCollapsed && !isMobile
                ? "max-w-0 opacity-0 overflow-hidden"
                : "max-w-full opacity-100"
            }`}
          >
            {item.label}
          </p>
        </div>
      );
    }

    // If icon is a React component, render it with conditional styling
    return React.cloneElement(item.icon, {
      className: isActive ? "text-white" : "text-purple-100",
    });
  };

  if (isLoading) {
    return (
      <div
        ref={ref}
        className="p-4 bg-white z-40 shadow-button_sm_hover w-[180px] h-max absolute left-0 top-0 flex-grow rounded-l-2xl"
      >
        <div className="flex items-center">
          <Image src="/images/Logo.jpg" alt="EL" />
        </div>
        <div className="flex flex-col justify-center items-center h-full gap-3">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isMobile && isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm"
          onClick={closeMobileMenu}
        />
      )}
      <div
        ref={ref}
        className={`${
          isMobile ? "fixed" : "absolute"
        } px-3 z-40 flex flex-col gap-2 bg-white top-0 right-0 justify-start items-center rounded-[10px] shadow-button_sm_hover transition-transform duration-300 ease-in-out`}
        style={{
          height: isMobile ? "100vh" : "calc(100vh - 40px)",
          width: `${containerWidth}px`,
          margin: isMobile ? "0px" : "20px",
          transform: isMobile
            ? `translateX(${isMobileMenuOpen ? 0 : containerWidth}px)`
            : "translateX(0)",
        }}
      >
        <div
          className="mt-5 mb-7 transition-all duration-300 ease-in-out cursor-pointer flex items-center justify-center"
          onClick={() => navigate("/")}
        >
          <Image
            src={isCollapsed ? "/icons/D-icon.svg" : "/icons/droplly-icon.svg"}
            alt="Logo"
            // className={isCollapsed ? "w-8 h-8" : "h-auto"}
            // width={isCollapsed ? 33 : 133}
          />
        </div>

        <TitleWithLines title="منوی اصلی" />

        {/* menu items */}
        <div className="flex flex-col gap-4 w-full">
          {menuItems.map((v, i) => {
            const isActive = v.route === location.pathname;
            return (
              <div
                onClick={() => (v.onClick ? v.onClick() : navigate(v.route))}
                onMouseEnter={() => setHover(() => v.label)}
                onMouseLeave={() => setHover(null)}
                key={i}
                className={`flex w-full flex-col ${
                  isCollapsed ? "items-center" : "items-start"
                } justify-end py-5 gap-2 px-3 rounded-xl hover:bg-gray-50 cursor-pointer ${
                  hover === v.label ? "bg-gray-50" : ""
                }`}
              >
                {/* <p className="text-purple-100 text-BodyText/14/Medium mr-2 max-sm:hidden">
                {v.label}
              </p> */}
                {renderIcon(v)}
              </div>
            );
          })}
        </div>

        {!isMobile && (
          <div
            className="flex items-center justify-center absolute -left-4 top-6 cursor-pointer"
            onClick={handleChevronClick}
          >
            <Image
              src="/icons/chevron-left.svg"
              alt="chevron-left"
              className="transition-transform duration-300 ease-in-out"
              style={{
                transform: `rotate(${isCollapsed ? 180 : 0}deg)`,
              }}
            />
          </div>
        )}
        {/* <Image
        className=" cursor-pointer max-sm:hidden bg-white rounded-full max-sm:w-10 max-sm:h-10"
        onClick={() => navigate("/profile")}
        width={50}
        src="/icons/profile2.svg"
      /> */}
      </div>
    </>
  );
}
